/** @jsxImportSource @emotion/react */
import {
  Box,
  CircularProgress,
  Paper,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import { useMemo } from 'react'
import { SLIDER_VALUES } from '../utils/Admin.constants'
import { FEATURE_FLAGS, FEATURE_FLAG_LABELS } from '@app/app-constants'
import { useFetchFeatureFlagRoles } from '@app/modules/Admin/hooks/useFetchFeatureFlagRoles'
import { useUpdateFeatureFlagRoles } from '@app/modules/Admin/hooks/useUpdateFeatureFlagRoles'
import { type FeatureFlagWithRoles, UserRole } from '@app/types'

// Get slider value from roles
const getSliderValueFromRoles = (roles: string[]): number => {
  if (roles.length === 0) return 0

  // Check which is the most permissive role
  if (roles.includes(UserRole.PUBLIC)) return 5
  if (roles.includes(UserRole.VIEWER)) return 4
  if (roles.includes(UserRole.MEMBER)) return 3
  if (roles.includes(UserRole.ADMIN)) return 2
  if (roles.includes(UserRole.STAFF)) return 1

  return 0
}

// Get roles from slider value
const getRolesFromSliderValue = (value: number): string[] => {
  const config = SLIDER_VALUES.find((v) => v.value === value)
  return config ? [...config.roles] : []
}

export const FeatureFlagsPage = () => {
  const theme = useTheme()
  const { data, isLoading } = useFetchFeatureFlagRoles()
  const updateMutation = useUpdateFeatureFlagRoles()

  // Merge known feature flags with API response
  const featureFlags = useMemo(() => {
    const apiFlags = data || []
    const knownFlagValues = Object.values(FEATURE_FLAGS)

    // Create a map of feature flags from API response
    const flagsMap = new Map<string, FeatureFlagWithRoles>()

    apiFlags.forEach((flag) => {
      if (flag?.feature) {
        flagsMap.set(flag.feature, flag)
      }
    })

    // Add known flags that aren't in the API response
    knownFlagValues.forEach((flagName) => {
      if (!flagsMap.has(flagName)) {
        flagsMap.set(flagName, {
          feature: flagName,
          user_roles: [],
        })
      }
    })

    // Convert to array and sort alphabetically
    return Array.from(flagsMap.values())
      .filter((flag) => flag?.feature)
      .sort((a, b) => a.feature.localeCompare(b.feature))
  }, [data])

  const handleSliderChange = (feature: string, newValue: number) => {
    const newRoles = getRolesFromSliderValue(newValue)

    updateMutation.mutate({
      feature,
      user_roles: newRoles,
    })
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          padding: '16px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 300px)',
          width: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 8, width: '100%' }}>
      <Box>
        <Box
          component={Paper}
          sx={{ p: 4, mb: 6, backgroundColor: theme.palette.background.default }}
        >
          <Typography variant="h5" sx={{ color: theme.palette.primary.dark, mb: 2 }}>
            Feature Flag Role Management
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.primary.dark }}>
            Configure minimum role required to access each feature flag. Slide from left (most
            restrictive: Staff only) to right (least restrictive: All users). Changes are saved
            automatically and will take effect after users refresh their session.
          </Typography>
        </Box>

        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '40%' }}>
                  <Typography
                    variant="body1"
                    sx={{ color: theme.palette.primary.dark, fontWeight: 'medium' }}
                  >
                    Feature Flag
                  </Typography>
                </TableCell>
                <TableCell sx={{ width: '60%' }}>
                  <Typography
                    variant="body1"
                    sx={{ color: theme.palette.primary.dark, fontWeight: 'medium' }}
                  >
                    Minimum Role
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {featureFlags.map((flag) => {
                const sliderValue = getSliderValueFromRoles(flag.user_roles)
                const displayLabel = FEATURE_FLAG_LABELS[flag.feature]

                return (
                  <TableRow key={flag.feature}>
                    <TableCell>
                      <Box>
                        {displayLabel && (
                          <Typography sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                            {displayLabel}
                          </Typography>
                        )}
                        <Typography
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: displayLabel ? '0.75rem' : '0.875rem',
                            color: displayLabel ? 'text.secondary' : 'text.primary',
                            mt: displayLabel ? 0.5 : 0,
                          }}
                        >
                          {flag.feature}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flex: 1, px: 2 }}>
                          <Slider
                            value={sliderValue}
                            onChange={(_, value) =>
                              handleSliderChange(flag.feature, value as number)
                            }
                            disabled={updateMutation.isPending}
                            min={0}
                            max={5}
                            step={1}
                            marks={SLIDER_VALUES.map((v) => ({
                              value: v.value,
                              label: v.label,
                            }))}
                            valueLabelDisplay="off"
                            sx={{
                              '& .MuiSlider-mark': {
                                backgroundColor: theme.palette.primary.main,
                                height: 8,
                                width: 2,
                              },
                              '& .MuiSlider-markActive': {
                                backgroundColor: theme.palette.primary.main,
                              },
                              '& .MuiSlider-markLabel': {
                                fontSize: '0.75rem',
                              },
                            }}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            minWidth: '60px',
                            fontWeight: 'bold',
                            color: theme.palette.primary.main,
                          }}
                        ></Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {featureFlags.length === 0 && (
          <Box sx={{ textAlign: 'center', padding: '32px' }}>
            <Typography variant="body1" color="text.secondary">
              No feature flags configured
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}
